import React, { Dispatch, SetStateAction, useContext, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Adaptive, RegularButton as Button, Typography, useSnackbar, useMediaQuery } from 'fronton-react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { DataContext, RouteContext, TInputParams } from 'entries/common/widgets/context';
import { Pencil } from 'assets';
import { gaClickOnGoods } from 'ga-events/ga-click-goods';
import { checkInputParamsInRooms } from 'pages/dimensions/helpers/check-input-params-in-rooms.helper';
import { roomArea, roomsAtom } from 'pages/common/store/rooms';
import { LayingTypeLabel } from 'pages/common/components/LayingTypeField/const';
import { useMaterialState } from '../surface/store/hooks/material';
import { useSurfaces } from '../surface/hooks/use-surfaces';
import { DoorsWindowsForm } from './forms/doors-windows/doors-windows.form';
import { RoomParametersForm } from './forms/room-parameters/room-parameters.form';
import { dimensionsAtom, dimensionsFormValues, TState } from './store/dimensions';
import { getFormBody } from './helpers/get-body';
import { useFormConditions } from './hooks/use-dimension-form-conditions';
import { Snackbar, SNACKBARS_DICTIONARY, TSnackbarDictionaryKeys } from '../common/components/Snackbar';
import { useSurfaceState } from '../surface/store/hooks/surface';
import { useFilteredInputParams } from './forms/hooks/use-filtered-input-params';
import { getFormData } from './helpers/get-form-data';
import { doorsWindowsArea } from './store/area';
import { WarningSnackBar } from './forms/warnong-snack-bar/warning-snack-bar.form';
import { getFormattedCalculateParams } from './helpers/get-formatted-calculate-params.helper';
import { hasWallAreaInRoom } from './helpers/has-not-wall-area-in-room';
import { hasAreaInRoom } from './helpers/has-area-in-room';
import { getActiveTab } from './helpers/get-active-tab';
import { getFormHeading } from './helpers/get-form-heading';
import { getFormSubheading } from './helpers/get-form-subheading';
import { getFilteredInputDefaultState } from './helpers/get-filtered-input-default-state';
import { useIsFloorCalculator } from './hooks/use-is-floor-calculator.hook';
import { Inputs } from './types';
import { MeasurementBanner } from './components/MeasurementBanner/measurement-banner.component';
import { SavedRooms } from './components/SavedRooms';
import { DimentionsFooter } from './components/DimentionsFooter';
import {
  FormStyled,
  DimensionsFormContainer,
  TextContainer,
  MaterialsContainer,
  MaterialInfoContainer,
  AreaFormContainerRow,
  FormContainer,
  StyledHeading,
  FooterWrapper,
  MeasurmentBannerWrapper,
  FormColumnRight,
  FormColumnLeft,
  FormRow,
  FormRowFooter,
} from './styles';
import { useGetRooms } from './hooks/use-get-rooms.hook';
import { getCookie } from '../../utils/cookie.helper';
import { getDefaultValues } from './helpers/get-default-values';
import { createConfiguration } from './helpers/create-configuration';
import { getIsShowRooms } from './helpers/get-is-show-rooms';
import { CUSTOMER_ID_COOKIE_NAME, UNAUTHORIZED_CUSTOMER_ID_COOKIE_NAME } from '../common/const';
import { useIsLaminateCalculator } from './hooks/use-is-laminate-calculator.hook';
import { setConfigurationIdCookie } from '../project-configuration/helpers/set-configuration-id-cookie';
import {
  projectUpdatedCustomEventNames,
  userProjectConfigurationCookieNames,
} from '../project-configuration/const';
import { dispatchProjectUpdatedCustomEvent } from '../project-configuration/helpers/dispatch-project-updated-custom-event';
import { getProjectUpdatedCustomEventPayload } from './helpers/get-project-updated-custom-event-payload';

export const DimensionsForm = () => {
  const { inputParameters, defaultInputParams, filteredSurfaces, hasLengthWidth } = useFilteredInputParams();
  const [isAreaEnabled] = useRecoilState(roomArea);
  const hasNotWallArea = hasWallAreaInRoom(filteredSurfaces);
  const hasArea = hasAreaInRoom({ inputParameters, hasLengthWidth, hasNotWallArea });

  const defaultState = getFilteredInputDefaultState({
    defaultInputParams,
    hasLengthWidth,
    isAreaEnabled,
    inputParameters,
    hasArea,
    hasNotWallArea,
  });

  const [filteredInputParams, setInputParams] = useState(defaultState);
  const [activeTabIndex, setActiveTabIndex] = useState(getActiveTab(filteredInputParams));
  const [hasNegativeParams, setHasNegativeParams] = useState<boolean | undefined>(false);
  const [showWarningBar, setShowWarningBar] = useState<boolean>(false);
  const { addSnack } = useSnackbar();
  const { setContext: setRoute } = useContext(RouteContext);
  const data = useContext(DataContext);
  const surfaces = useSurfaces();

  const isFloorCalculatorId = useIsFloorCalculator();
  const isLaminateCalculatorId = useIsLaminateCalculator();

  const { surfaceFormState } = useSurfaceState();
  const { materialFormState } = useMaterialState();

  const { hasDoorsWindows, hasThickness, hasMaterialsHeader, hasDoors } = useFormConditions();

  const saveCalculateParams = useSetRecoilState(dimensionsAtom);
  const [dimensionState, setDimensionState] = useRecoilState(dimensionsFormValues);
  const isRoomArea = useRecoilValue(roomArea);
  const isDoorsWindowsArea = useRecoilValue(doorsWindowsArea);

  const isMobile = useMediaQuery({
    maxWidth: 767,
  });

  const defaultValues = getDefaultValues({ hasDoors, hasThickness, dimensionState });

  const methods = useForm<Inputs>({
    defaultValues,
  });

  useGetRooms();
  const calculatedRooms = useRecoilValue(roomsAtom);
  const setRoomsState = useSetRecoilState(roomsAtom);

  const isShowRooms = getIsShowRooms({ calculatedRooms, isFloorCalculatorId });

  const onSubmit: SubmitHandler<Inputs> = async (inputsData: Inputs) => {
    const apiMethods = data!.api;
    const formData = getFormData({
      data: inputsData,
      state: {
        isDoorsWindowsArea,
        isRoomArea,
      },
    });
    const floorTypeInstallation = data?.surfaces.surface?.FLOOR?.enums?.typeInstallation;
    const calculationCoefficient =
      floorTypeInstallation?.[LayingTypeLabel[formData?.thickness as 'horizontal' | 'diagonal']];

    const body = getFormBody({
      data: formData,
      surfaces: { state: surfaceFormState, data: surfaces },
      materials: materialFormState,
      productData: data,
    });

    if (isFloorCalculatorId && data) {
      const thickness = methods.getValues('thickness');
      const checkData = checkInputParamsInRooms({
        inputParams: body.inputParameters,
        calculatorId: data.surfaces.id,
        typeInstallation: thickness,
        rooms: calculatedRooms,
      });
      if (checkData.needToUpdateRooms) {
        const authorizedCustomerId = getCookie(CUSTOMER_ID_COOKIE_NAME);
        const unauthorizedCustomerId = getCookie(UNAUTHORIZED_CUSTOMER_ID_COOKIE_NAME);
        const isAnyCustomerIdExists = Boolean(authorizedCustomerId) || Boolean(unauthorizedCustomerId);
        if (isAnyCustomerIdExists) {
          createConfiguration({
            data,
            inputParameters: body.inputParameters,
            thickness,
            authorizedCustomerId,
            unauthorizedCustomerId,
            isLaminateCalculatorId,
            calculationCoefficient,
          });
          setRoomsState(checkData.newRooms);
        }
      } else if (checkData.sameRooms.length > 0 && isLaminateCalculatorId) {
        setConfigurationIdCookie({
          projectConfigurationId: checkData.sameRooms[0],
          projectConfigurationCookieName: userProjectConfigurationCookieNames.LAMINATE,
        });
      }
      if (isLaminateCalculatorId) {
        const projectUpdatedCustomEventPayload = {
          projectConfigurationParameters: getProjectUpdatedCustomEventPayload({
            inputsData: formData,
            calculationCoefficient,
            calculatorId: data?.surfaces?.id,
          }),
        };
        dispatchProjectUpdatedCustomEvent({
          projectUpdatedCustomEventName: projectUpdatedCustomEventNames.LAMINATE,
          projectUpdatedCustomEventPayload,
        });
      }
    }

    try {
      const calculateParams: TState = await apiMethods.post(`/calculate/${data?.productId}`, body);
      const formattedCalculateParams = getFormattedCalculateParams(calculateParams);
      gaClickOnGoods({ title: 'Ввод размеров', name: 'Рассчитать количество' });
      setDimensionState(formData);
      saveCalculateParams(formattedCalculateParams);
      setRoute('calculation');
    } catch (e) {
      addSnack(SNACKBARS_DICTIONARY[TSnackbarDictionaryKeys.CALCULATION_ERROR]);
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  const isHaveComplimentaryProduct = !!data?.surfaces.complimentaryProducts.length;

  return (
    <DimensionsFormContainer>
      {hasMaterialsHeader && (
        <Adaptive from="tablet">
          <TextContainer>
            <Typography variant="caption" color="text-minor">
              Выбранные поверхности
            </Typography>
            <Button
              variant="secondary"
              label="Изменить поверхности"
              size="s"
              onClick={() => {
                gaClickOnGoods({ title: 'Ввод размеров', name: 'Изменить поверхности' });
                setRoute('surface');
              }}
              iconLeft={<Pencil />}>
              Изменить поверхности
            </Button>
          </TextContainer>
          <MaterialsContainer>
            {filteredSurfaces.map(surface => (
              <MaterialInfoContainer key={surface.name}>
                <Typography variant="m" color="text-primary" size="body_long">
                  {surface.label}
                  {materialFormState[surface.name]?.value ? ':' : null}
                </Typography>
                {materialFormState[surface.name]?.value && (
                  <Typography variant="m" color="text-secondary" size="body_long">
                    {materialFormState[surface.name].value}
                  </Typography>
                )}
              </MaterialInfoContainer>
            ))}
          </MaterialsContainer>
        </Adaptive>
      )}
      <FormProvider {...methods}>
        <FormStyled id="dimensions-form" onSubmit={methods.handleSubmit(onSubmit)}>
          <FormContainer>
            <AreaFormContainerRow hasDoorsWindows={hasDoorsWindows}>
              <StyledHeading>{getFormHeading(isMobile)}</StyledHeading>
              {getFormSubheading(isMobile, isHaveComplimentaryProduct)}
            </AreaFormContainerRow>
            {isShowRooms ? (
              <AreaFormContainerRow>
                <SavedRooms setInputParams={setInputParams} setActiveTabIndex={setActiveTabIndex} />
              </AreaFormContainerRow>
            ) : null}
            <FormRow>
              <FormColumnLeft>
                <AreaFormContainerRow>
                  <RoomParametersForm
                    hasNegativeParams={hasNegativeParams}
                    setHasNegativeParams={setHasNegativeParams}
                    setShowWarningBar={setShowWarningBar}
                    filteredInputParams={filteredInputParams}
                    setInputParams={setInputParams as Dispatch<SetStateAction<TInputParams>>}
                    hasArea={hasArea}
                    activeTabIndex={activeTabIndex}
                    setActiveTabIndex={setActiveTabIndex}
                  />
                </AreaFormContainerRow>
                {hasDoorsWindows && (
                  <AreaFormContainerRow>
                    <DoorsWindowsForm />
                  </AreaFormContainerRow>
                )}
              </FormColumnLeft>
              <FormColumnRight>
                <MeasurmentBannerWrapper>
                  <MeasurementBanner />
                </MeasurmentBannerWrapper>
              </FormColumnRight>
            </FormRow>
            <FormRowFooter>
              <FooterWrapper>
                <DimentionsFooter />
              </FooterWrapper>
            </FormRowFooter>
          </FormContainer>

          {showWarningBar && <WarningSnackBar />}
        </FormStyled>
      </FormProvider>
      <Snackbar />
    </DimensionsFormContainer>
  );
};
